<script setup>
import { Dialog, DialogPanel, TransitionChild, TransitionRoot } from '@headlessui/vue'
import InputLabel from "@/Components/InputLabel.vue";
import PrimaryButton from "@/Components/PrimaryButton.vue";
import {computed, onMounted, reactive, ref, watch} from "vue";
import TextInput from "@/Components/TextInput.vue";
import SimpleComboBox from "@/Components/SimpleComboBox.vue";
import {useErrorStore} from "@/Stores/errorStore";
import {useStatusStore} from "@/Stores/statusStore";
import {usePromptStore} from "@/Stores/promptStore";
import {useUserStore} from "@/Stores/userStore";
import {useCreditStore} from "@/Stores/creditStore";
import DieIcon from "@/Components/Icons/DieIcon.vue";
import {FontAwesomeIcon} from "@fortawesome/vue-fontawesome";
import TextComboBox from "@/Components/TextComboBox.vue";
import PermissionConstants from "@/Constants/permission-constants";
import { generateMerchantCollection } from "@/Utils/EquipmentDescriptionGenerator.js";
import SubscriptionPricingModal from "@/Components/Subscriptions/SubscriptionPricingModal.vue";
import ArtStyleSelector from "@/Components/GenerationForms/ArtStyleSelector.vue";

const emits = defineEmits(['close', 'generate-stash']);
const showPricingModal = ref(false);
const errorStore = useErrorStore();
const statusStore = useStatusStore();
const promptStore = usePromptStore();
const userStore = useUserStore();
const creditStore = useCreditStore();

const creditCost = computed(() => {
    const quantity = parseInt(data.request.quantity) || 1;
    return creditStore.costs.equipment * quantity;
});
const canAfford = computed(() => {
    if (creditStore.unlimited) return true;
    return creditStore.currentBalance >= creditCost.value;
});

const props = defineProps({
    show: Boolean,
    title: {
        type: String,
        default: "Generate Equipment"
    }
});

const qualities = [
    {'id': 1, 'value': "garbage"},
    {'id': 2, 'value': "worn"},
    {'id': 3, 'value': "common"},
    {'id': 4, 'value': "uncommon"},
    {'id': 5, 'value': "rare"},
    {'id': 6, 'value': "exquisite"},
    {'id': 7, 'value': "epic"},
    {'id': 8, 'value': "legendary"},
    {'id': 9, 'value': "artifact"},
    {'id': 10, 'value': "mythic"}
];

const quantities = [
    {'id': 1, 'value': "1"},
    {'id': 2, 'value': "2"},
    {'id': 3, 'value': "3"},
    {'id': 4, 'value': "4"},
];

const editions = [
    {'id': 1, 'value': "5e", 'label': "D&D 5e"},
    {'id': 2, 'value': "Pf2", 'label': "Pathfinder 2e"},
    {'id': 3, 'value': "toa", 'label': "Tavern of Azoth"},
];

const visibleEditions = computed(() => {
    if (!userStore.hasPermission(PermissionConstants.permissions.campaignStudioAccess)) {
        return editions.filter(edition => edition.value !== "toa");
    }
    return editions;
});

const equipmentLevels = [
    {'id': 0, 'value': "None"},
    {'id': 1, 'value': "Garbage"},
    {'id': 2, 'value': "Basic"},
    {'id': 3, 'value': "Advanced"},
    {'id': 4, 'value': "Magic"},
    {'id': 5, 'value': "Rare"},
    {'id': 6, 'value': "Artifact"},
];

const artStyles = [
    'Dark Fantasy', 'High Fantasy', 'Sketch', 'Ethereal Dream',
    'Realism', 'Pixel Art', 'Graphic Novel', 'Concept Art'
];

const data = reactive({
    request: {
        influence: "",
        quality: "",
        quantity: quantities[0].value,
        style: "Dark Fantasy",
        edition: editions[0].value,
    },
    placeholders: {
        influence: "Describe these items. (e.g. 'items that are useful for a thief')",
        quality: "Select equipment level",
        quantity: "Describe the quantity",
        tier: "Select a tier",
    },
    editions: visibleEditions,
})

function generateStash() {
    if (!canAfford.value) {
        showPricingModal.value = true;
        return;
    }
    emits('generate-stash', data.request);
}

function close() {
    emits('close');
}

function onDieRoll() {
    const generated = generateMerchantCollection();
    data.request.influence = generated.description;
    data.request.quantity = String(generated.quantity);
    data.request.style = artStyles[Math.floor(Math.random() * artStyles.length)];

    const weightedIndices = [1, 2, 2, 2, 2, 2, 3, 4]; // 2 is weighted 5 times (It's the preferred result)
    const randomIndex = weightedIndices[Math.floor(Math.random() * weightedIndices.length)];
    data.placeholders.quality = equipmentLevels[randomIndex];
}

const isAdmin = computed(() => {
    return !!userStore.hasPermission(PermissionConstants.permissions.admin);
})

onMounted(() => {
    const initialGenerated = generateMerchantCollection();
    data.placeholders.influence = initialGenerated.description;
    data.placeholders.quality = equipmentLevels[2];
    data.placeholders.quantity = String(initialGenerated.quantity);
    errorStore.clearErrors()


    console.log(promptStore.equipment)
    console.log(promptStore.equipment.quality)

    const keys = ["influence", "style", "edition", "quantity"];
    keys.forEach(key => {
        if (promptStore.equipment[key] != null) {
            data.request[key] = promptStore.equipment[key];
        }
    });
    if (promptStore.npc.quality != null) {
        const quality = equipmentLevels.find(proficiency => proficiency.value === promptStore.npc.quality);
        data.placeholders.quality = quality
    }

})

watch(
    () => data.request,
    (updated) => {
        promptStore.equipment.influence = updated.influence;
        promptStore.equipment.edition = updated.edition;
        promptStore.equipment.style = updated.style;
        promptStore.equipment.quality = updated.quality;
        promptStore.equipment.quantity = updated.quantity;
    },
    { deep: true }
);

</script>

<template>
    <SubscriptionPricingModal
        :show="showPricingModal"
        :highlight-feature="'credits'"
        :title="'Need more credits?'"
        @close="showPricingModal = false"
    />
    <TransitionRoot as="template" :show="show != null">
        <Dialog as="div" class="relative z-10" @close="$emit('close')">
            <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0" enter-to="opacity-100" leave="ease-in duration-200" leave-from="opacity-100" leave-to="opacity-0">
                <div class="fixed inset-0 bg-gray-800 bg-opacity-75 transition-opacity" />
            </TransitionChild>

            <div class="mt-0 sm:mt-8 fixed inset-0 z-10 overflow-y-auto">
                <div class="flex min-h-full items-start justify-center text-center sm:items-center sm:p-4 p-0">
                    <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95" enter-to="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-200" leave-from="opacity-100 translate-y-0 sm:scale-100" leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
                        <DialogPanel class="w-full sm:w-9/12 max-w-2xl bg-gray-750 rounded-lg shadow-lg">
                            <!-- Header -->
                            <div class="text-left text-white p-4 border-b border-gray-700">
                                <div class="flex justify-between items-center">
                                    <p class="text-gray-300 font-semibold text-2xl"><strong>{{ props.title }}</strong></p>
                                    <div class="flex items-center gap-2">
                                        <DieIcon @animation-finished="onDieRoll" aria-label="Randomize"/>
                                        <FontAwesomeIcon
                                            :icon="['fas', 'xmark']"
                                            class="cursor-pointer"
                                            size="lg"
                                            style="color: #ffffff;"
                                            @click="close"
                                        />
                                    </div>
                                </div>
                            </div>

                            <!-- Content -->
                            <div class="p-6 bg-gray-700 text-left text-gray-100 rounded-b-lg">
                                <!-- Art Style Selector - Full Width -->
                                <div class="mb-4">
                                    <ArtStyleSelector
                                        content-type="equipment"
                                        v-model="data.request.style"
                                    />
                                </div>

                                <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div class="sm:col-span-2">
                                        <InputLabel id="influence" for="influence" value="Description (Be Creative)" class="leading-6"/>
                                        <textarea id="influence" class="block w-full pl-4 pt-2 border-0 dark-input mt-1" v-model="data.request.influence" :placeholder="data.placeholders.influence" maxlength="1024"/>
                                    </div>
                                    <div>
                                        <TextComboBox label="Equipment Level" :options="equipmentLevels" :default-option="data.placeholders.quality" :placeholder="'Equipment Level'" @selected="data.request.quality = $event" @updated="data.request.quality = $event"/>
                                    </div>
                                    <div>
                                        <SimpleComboBox :key="data.request.quantity" label="Quantity" :options="quantities" :default-option="quantities.find(quantity => quantity.value === data.request.quantity)" :placeholder="data.placeholders.quantity" @selected="data.request.quantity = $event"/>
                                    </div>
                                    <div class="sm:col-span-2">
                                        <SimpleComboBox label="Edition" :options="data.editions" :default-option="data.editions.find(edition => edition.value === data.request.edition)" :placeholder="data.placeholders.edition" @selected="data.request.edition = $event"/>
                                    </div>
                                    <div class="sm:col-span-2 flex justify-end mt-2">
                                        <div class="flex flex-col items-end">
                                            <h3 class="w-full text-center">
                                                <span v-if="statusStore.state?.remainingTime > 0"> (Wait {{ statusStore.state?.remainingTime }}s)</span>
                                            </h3>
                                            <PrimaryButton class="!bg-blue-600" @click="generateStash" :disabled="statusStore.state?.remainingTime > 0">
                                                Generate
                                                <span v-if="!creditStore.unlimited" class="ml-1 opacity-75">
                                                    ({{ creditCost }})
                                                </span>
                                            </PrimaryButton>
                                            <p v-if="!creditStore.unlimited" class="text-xs text-gray-400 mt-1">
                                                {{ creditStore.currentBalance }} credits remaining
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </DialogPanel>
                    </TransitionChild>
                </div>
            </div>
        </Dialog>
    </TransitionRoot>
</template>

<style>
/* Styles removed - chevron toggle no longer needed */
</style>

